import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, BellOff, Volume2, Globe2 } from "lucide-react";
import { SOUND_OPTIONS, playNotificationSound, ensureNotificationPermission } from "@/lib/notify";

const TIMEZONES = [
  "UTC",
  "America/New_York",   // EST/EDT
  "America/Chicago",    // CST/CDT
  "America/Denver",     // MST/MDT
  "America/Los_Angeles",// PST/PDT
  "America/Anchorage",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",       // IST
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const LABELS = {
  "America/New_York": "Eastern (EST/EDT)",
  "America/Chicago": "Central (CST/CDT)",
  "America/Denver": "Mountain (MST/MDT)",
  "America/Los_Angeles": "Pacific (PST/PDT)",
  "Asia/Kolkata": "India (IST)",
};

export default function PreferencesPanel() {
  const { refresh } = useAuth();
  const [prefs, setPrefs] = useState({ timezone: "UTC", notification_sound: "chime", show_timezone_footer: true });
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/preferences");
        setPrefs(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/preferences", patch);
      setPrefs(data);
      toast.success("Preferences saved");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const requestPerm = async () => {
    const p = await ensureNotificationPermission();
    setPermission(p);
    if (p === "granted") toast.success("Browser notifications enabled");
    else if (p === "denied") toast.error("Notifications blocked in browser settings");
  };

  const localNow = (() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: prefs.timezone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(new Date());
    } catch {
      return "—";
    }
  })();

  return (
    <div className="space-y-4" data-testid="preferences-panel">
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Globe2 className="h-3 w-3" /> Timezone</Label>
        <Select value={prefs.timezone} onValueChange={(v) => save({ timezone: v })}>
          <SelectTrigger className="rounded-xl mt-1.5" data-testid="pref-timezone-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            {TIMEZONES.map((tz) => (
              <SelectItem key={tz} value={tz}>{LABELS[tz] || tz}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-[11px] font-mono text-muted-foreground mt-1.5" data-testid="pref-local-now">Now: {localNow}</div>
      </div>

      <div className="flex items-center justify-between border border-border rounded-xl px-3 py-2.5">
        <div>
          <div className="text-sm font-medium">Append timezone footer</div>
          <div className="text-[11px] text-muted-foreground">Adds “Sent from {prefs.timezone} · local time” at bottom of outgoing emails.</div>
        </div>
        <Switch checked={prefs.show_timezone_footer} onCheckedChange={(v) => save({ show_timezone_footer: v })} disabled={busy} data-testid="pref-timezone-footer-toggle" />
      </div>

      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Volume2 className="h-3 w-3" /> Notification sound</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Select value={prefs.notification_sound} onValueChange={(v) => save({ notification_sound: v })}>
            <SelectTrigger className="rounded-xl flex-1" data-testid="pref-sound-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOUND_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => playNotificationSound(prefs.notification_sound)} data-testid="pref-sound-preview">Preview</Button>
        </div>
      </div>

      <div className="border border-border rounded-xl px-3 py-2.5 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium flex items-center gap-1.5">
            {permission === "granted" ? <Bell className="h-3.5 w-3.5 text-emerald-brand" /> : <BellOff className="h-3.5 w-3.5 text-muted-foreground" />}
            Browser push notifications
          </div>
          <div className="text-[11px] text-muted-foreground">
            {permission === "granted" ? "Enabled — you'll get OS notifications when this tab is hidden." :
             permission === "denied" ? "Blocked. Update your browser site settings to enable." :
             "Allow browser popups for new emails and AI activity."}
          </div>
        </div>
        {permission !== "granted" && (
          <Button size="sm" className="rounded-xl" onClick={requestPerm} data-testid="pref-permission-request">Enable</Button>
        )}
      </div>
    </div>
  );
}
